<template>
<div
  class='activity-app lesson-container'
  :class="{'is-fullscreen': is_fullscreen }"
>
  <!-- Preloader -->
  <div
    v-if='!is_loaded'
    class='activity-app_loader'
  >
    <div class='activity-app_loader-inner' />
  </div>

  <!-- Main Content -->
  <template v-if='is_loaded'>
    <!-- Logo -->
    <div
      v-if='!info_opened'
      class='activity-app_logo'
    >
      <img
        v-if='activity.configuration && activity.configuration.partner_logo'
        :src='activity.configuration.partner_logo'
        alt=''
      >
    </div>

    <!-- Right Menu -->
    <div
      v-if='!info_opened && has_info_overlay'
      class='activity-app_actions'
    >
      <a
        v-if='has_info_overlay'
        class='activity-app_info-toggle'
        href='#info'
        @click.prevent='toggleInfoOverlay'
      >
        Show Info
        <IconInfo />
      </a>
    </div>

    <!-- Activity Slide -->
    <component
      :is='slideType(activity)'
      :activity='activity'
      :info_opened='info_opened'
      :is_authenticated='is_authenticated'
      :slide_result='slide_result'
      :toggleActivityOverlay='toggleActivityOverlay'
      :toggleResourceOverlay='toggleResourceOverlay'
      :toggleSignInOverlay='toggleSignInOverlay'
      :updateResult='updateSlideResult'
    />

    <!-- Info Overlay -->
    <InfoOverlay
      v-if='has_info_overlay && info_opened'
      :overlay='activity.overlay'
      :resources='activity.resources'
      :tbk_label_visible='activity.configuration && activity.configuration.tbk_label_visible'
      :image='activity.background_image'
      :toggleInfoOverlay='toggleInfoOverlay'
      :toggleResourceOverlay='toggleResourceOverlay'
    />

    <!-- Activity Overlay -->
    <ActivityOverlay
      v-if='activity_opened'
      :api_client='api_client'
      :activity='activity'
      :slide_result='slide_result'
      :is_authenticated='is_authenticated'
      :toggleActivityOverlay='toggleActivityOverlay'
      :toggleSignInOverlay='toggleSignInOverlay'
      :updateResult='updateSlideResult'
      :updateCurrentActivity='updateCurrentActivity'
    />
  </template>

  <!-- Sign In Overlay -->
  <SignInOverlay
    v-if='sign_in_overlay_opened'
    :toggleSignInOverlay='toggleSignInOverlay'
  />

  <!-- Resource Overlay -->
  <transition
    mode='out-in'
    name="fast-fade"
  >
    <ResourceOverlay
      v-if='active_resource'
      :closeOverlay='toggleResourceOverlay'
      :resource='activityToResourceObject()'
    />
  </transition>

  <!-- Cookies consent -->
  <CookiesConsent
    is_embed
    :enabled='show_cookie_notice'
    :subdomain='subdomain'
  />

  <!-- Custom Styles -->
  <CustomStyles />

  <URLCustomStyles
    v-if='custom_styles && Object.keys(custom_styles).length'
    :brand_color='custom_styles.color'
    :font_family='custom_styles.font'
    :font_url='custom_styles.font_url'
    :headings_family='custom_styles.headings'
    :headings_url='custom_styles.headings_url'
  />
</div>
</template>

<script>
import { inject } from 'vue'

import apiClient from '../api'
import ModelLoader from '../models/models_activity.js'

import SlideTypesMixin from '../slides/slide-types-mixin'

import CookiesConsent from 'components/Shared/CookiesConsent/CookiesConsent'
import InfoOverlay from './info-overlay'
import ActivityOverlay from './activity-overlay'
import ResourceOverlay from './ResourceOverlay'
import SignInOverlay from './sign-in-overlay'
import CustomStyles from 'components/Shared/CustomStyles/CustomStyles'
import URLCustomStyles from '../course/custom-styles'

import IconInfo from '../icons/information-circle.svg'

import whatInput from 'what-input' // eslint-disable-line no-unused-vars

export default {
  mixins: [SlideTypesMixin],

  components: {
    CookiesConsent,
    InfoOverlay,
    ActivityOverlay,
    ResourceOverlay,
    SignInOverlay,
    CustomStyles,
    URLCustomStyles,
    IconInfo
  },

  data() {
    return {
      jwt: null,
      custom_styles: {},
      api_url: null,
      subdomain: null,
      activity_id: null,
      global_slide_id: null,
      theme: null,
      locale: null,
      show_cookie_notice: null
    }
  },

  created() {
    this.options = inject('options')
    this.configuration = inject('configuration')

    this.jwt = this.options.jwt
    this.custom_styles = this.options.custom_styles
    this.api_url = this.options.api_url
    this.subdomain = this.options.subdomain
    this.activity_id = this.options.activity_id
    this.global_slide_id = this.options.global_slide_id
    this.theme = this.options.theme
    this.locale = this.options.locale
    this.show_cookie_notice = this.options.show_cookie_notice
    this.orientation = this.options.orientation
  },

  mounted() {
    apiClient.setUrl(this.api_url)
    apiClient.setSubdomain(this.subdomain)
    apiClient.useCookies(false)
    this.is_authenticated && apiClient.setToken(this.jwt)
    // Set locale
    if (this.locale) {
      this.$i18n.locale = this.locale
      apiClient.setLocale(this.locale)
    }
    this.api_client = apiClient
    ModelLoader.load(this.api_client)

    // IOS Detection
    let isIos = false
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
      isIos = true
    } else if (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform)) {
      isIos = true
    }

    if (isIos) {
      $('body').addClass('is-ios-device')
    }

    this.fetchData(() => {
      this.initResults()

      this._setPageTitle()
    })
  },

  data() {
    return {
      is_loaded: false,
      api_client: null,
      activity: null,

      info_opened: false,
      activity_opened: false,
      sign_in_overlay_opened: false,

      active_resource: null,

      slide_result: {
        status: 'started',
        results: {}
      },
      events: ['viewed', 'clicked', 'listened', 'watched', 'commented', 'submitted', 'finished', 'retake']
    }
  },

  watch: {
    'slide_result.updated_at': function(value) {
      console.log('result updated', value)
      this._updateLocalStorage()
    }
  },

  computed: {
    storage_key() {
      return `tbk_global_slide_${this.global_slide_id}_activity_${this.activity_id}`
    },

    is_fullscreen() {
      return window.parent.location === window.self.location
    },

    has_cta() {
      return this.activity.overlay && Object.keys(this.activity.overlay).length
    },

    has_resources() {
      return !!(this.activity.resources && this.activity.resources.length)
    },

    has_info_overlay() {
      return this.has_cta || this.has_resources
    },

    is_authenticated() {
      return this.jwt !== null && this.jwt !== undefined && this.jwt.length > 0
    },

    has_localstorage_access() {
      try {
        const storage = window.localStorage
        const test = '__storage_test__'
        storage.setItem(test, test)
        storage.removeItem(test)
        return true
      } catch (e) {
        return false
      }
    }
  },

  methods: {
    fetchData(callback) {
      const activity_url = `${this.api_client.apiUrl}/global_slides/${this.global_slide_id}/activity`
      this.api_client.request(activity_url, 'GET')
        .then(({ data }) => {
          this.activity = data
          // A ?orientation= URL param overrides the authored content-level
          // Hype orientation (PDEV-23436). The backend only injects one of the
          // supported values, so a non-empty value is safe to apply directly.
          // Both Hype rendering (_hypeOrientation) and carousel suppression
          // (is_vertical_hype) read activity.hype_orientation, so setting it
          // here is all that's needed.
          if (this.orientation) {
            this.activity.hype_orientation = this.orientation
          }
          callback()
        })
        .catch((error) => {
          console.log(error)
        })
    },

    initResults() {
      // search for saved results in localStorage
      const local_storage_result = this._getLocalStorageResults(this.storage_key)
      if (local_storage_result) {
        this._updateCurrentState(local_storage_result)
      } else {
        // init new results
        this.slide_result.status = 'started'
        this.slide_result.results = {}
      }

      if (this.is_authenticated) {
        this._getResultsFromDB('global_slide_activity_results', () => (this.is_loaded = true))
      } else {
        this.is_loaded = true
      }
    },

    toggleInfoOverlay() {
      this.info_opened = !this.info_opened
    },

    toggleActivityOverlay() {
      this.activity_opened = !this.activity_opened
    },

    toggleResourceOverlay(resource) {
      this.active_resource = resource || null
    },

    toggleSignInOverlay() {
      if (this.is_authenticated) return false
      this.sign_in_overlay_opened = !this.sign_in_overlay_opened
    },

    activityToResourceObject() {
      if (this.active_resource.activity) {
        return this.active_resource
      } else {
        const resource = {
          headline: this.active_resource.headline,
          activity: this.active_resource
        }
        return resource
      }
    },

    updateSlideResult(event, result = {}, files = null, options = {}) {
      const slideResult = Object.assign({}, this.slide_result) // we need copy of the object
      if (!result.hasOwnProperty('has_consented') && !this._needsUpdate(slideResult, event)) return

      slideResult.updated_at = Math.round(Date.now() / 1000)
      slideResult.event = event
      slideResult.results = result

      if (files) {
        slideResult.files = files
      }

      if (this.is_authenticated) {
        const params = { slide_id: this.global_slide_id }
        // sometimes we need to include activity object
        // to the slide_activity_result object
        if (options.include) {
          // params['include'] = options.include
          // TODO: fix
          params.include = 'global_slide.activity'
        }

        this.api_client.create('global_slide_activity_result', slideResult, params)
          .then(({ data }) => {
            this._updateCurrentState(data)
            if (typeof (options.callback) === 'function') {
              options.callback(data)
            }
          })
          .catch((error) => {
            console.log(error)
            this._updateCurrentState(slideResult)
          })
      } else {
        slideResult.status = 'passed'
        this._updateCurrentState(slideResult)
      }
    },

    updateCurrentActivity(activity) {
      // find current activity and update it's answers
      this.activity.answers = activity.answers
    },

    _needsUpdate(prevResult, newEvent) {
      return (this.events.indexOf(prevResult.event) < this.events.indexOf(newEvent)) ||
        (
          (prevResult.status === 'passed' || prevResult.status === 'failed')          &&
          (newEvent === 'retake' || newEvent === 'finished' || newEvent === 'submitted')
        )
    },

    _getLocalStorageResults(key) {
      if (!this.has_localstorage_access) return false

      const results = localStorage.getItem(key)
      return JSON.parse(results)
    },

    _getResultsFromDB(results_type, callback) {
      this.api_client.findAll(results_type, { 'filter[slides]': this.global_slide_id })
        .then(({ data }) => {
          data.forEach(sar => this._updateCurrentState(sar))
          callback()
        })
        .catch((error) => {
          console.log(error)
          callback()
        })
    },

    _updateCurrentState(result) {
      this.slide_result.event = result.event
      this.slide_result.status = result.status
      this.slide_result.results = result.results
      result.updated_at && (this.slide_result.updated_at = result.updated_at)
    },

    _updateLocalStorage() {
      if (this.has_localstorage_access) {
        localStorage.setItem(this.storage_key, JSON.stringify(this.slide_result))
      }
    },

    _setPageTitle() {
      let title = ''; let label = ''; let headline = ''

      switch (this.activity.type) {
        case 'activity_v1_articles':
          label = this.activity.label || this.$t('article')
          break
        case 'activity_v1_audios':
          label = this.activity.label || this.$t('audio')
          break
        case 'activity_v1_assessments':
          label = this.activity.label || this.$t('assessment')
          break
        case 'activity_v1_basic_contents':
          label = this.activity.label || this.$t('basic_content')
          break
        case 'activity_v1_discussions':
          label = this.activity.label || this.$t('discussion')
          break
        case 'activity_v1_fullscreens':
          label = this.activity.label || this.$t('quote')
          break
        case 'activity_v1_intention_setters':
          label = this.activity.label || this.$t('intentions.intention_setter')
          break
        case 'activity_v1_journals':
          label = this.activity.label || this.$t('journal')
          break
        case 'activity_v1_paired_comparisons':
          label = this.activity.label || this.$t('paired_comparison')
          break
        case 'activity_v1_polls':
          label = this.activity.label || this.$t('poll')
          break
        case 'activity_v1_profiles':
          label = this.activity.label || this.activity.subheadline
          break
        case 'activity_v1_quizzes':
          label = this.activity.label || this.$t('quiz')
          break
        case 'activity_v1_self_assessments':
          label = this.activity.label || this.$t('self_assessment')
          break
        case 'activity_v2_self_assessments':
          label = this.activity.label || this.$t('self_assessment')
          break
        case 'activity_v1_resources':
          label = this.activity.label || this.$tc('resource')
          break
        case 'activity_v1_video_with_instructions':
          label = this.activity.label || this.activity.subheadline
          break
        case 'activity_v1_videos':
          label = this.activity.label || this.$t('video')
          break
        default:
          label = this.activity.label || this.$t('information')
      }

      if (this.activity.type === 'activity_v1_basic_contents' && !this.activity.headline) {
        headline = this.$t('additional_content')
      } else {
        headline = this.activity.headline
      }

      title = label + ': ' + headline
      if (this.activity.type === 'activity_v1_basic_contents') {
        title = headline + ': ' + label
      }

      document.title = title
    }
  }
}
</script>

<style lang='sass'>
@import '../styles/mixins/styleguide/links'
@import '../styles/mixins/styleguide/typography'
@import '../styles/mixins/styleguide/buttons'
@import '../styles/mixins/styleguide/vh-based-typography'
@import '../styles/mixins/styleguide/default'
</style>

<style lang='sass'>
// betterfly
@import "lesson/styles/customizations/new/betterfly"
@import "../styles/mixins/styleguide/lesson/vh-based-betterfly"

// carebridge
@import "lesson/styles/customizations/new/carebridge"
@import "../styles/mixins/styleguide/lesson/vh-based-carebridge"

// covidvax
@import "lesson/styles/customizations/new/covidvax"
@import "../styles/mixins/styleguide/lesson/vh-based-covidvax"

// mobe
@import "lesson/styles/customizations/new/mobe"
@import "../styles/mixins/styleguide/lesson/vh-based-mobe"

// nps
@import "lesson/styles/customizations/new/nps"
@import "../styles/mixins/styleguide/lesson/vh-based-nps"

// light
@import "lesson/styles/customizations/new/light"
@import "../styles/mixins/styleguide/lesson/vh-based-light"

// ula
@import "lesson/styles/customizations/new/ula"
@import "../styles/mixins/styleguide/lesson/vh-based-ula"

// phs
@import "lesson/styles/customizations/new/phs"
@import "../styles/mixins/styleguide/lesson/vh-based-phs"

// phs-optum
@import "lesson/styles/customizations/new/phs-optum"
@import "../styles/mixins/styleguide/lesson/vh-based-phs-optum"

// phs-uhc
@import "lesson/styles/customizations/new/phs-uhc"
@import "../styles/mixins/styleguide/lesson/vh-based-phs-uhc"

// optumbank
@import "lesson/styles/customizations/new/optumbank"
@import "../styles/mixins/styleguide/lesson/vh-based-optumbank"

// rally
@import "lesson/styles/customizations/new/rally"
@import "../styles/mixins/styleguide/lesson/vh-based-rally"

// medela
@import "lesson/styles/customizations/new/medela"
@import "../styles/mixins/styleguide/lesson/vh-based-medela"

// mindfull
@import "lesson/styles/customizations/new/mindfull"
@import "../styles/mixins/styleguide/lesson/vh-based-mindfull"

// usbank
@import "lesson/styles/customizations/new/usbank"
@import "../styles/mixins/styleguide/lesson/vh-based-usbank"

// walmart
@import "lesson/styles/customizations/new/walmart"
@import "../styles/mixins/styleguide/lesson/vh-based-walmart"
</style>

<style lang='sass' scoped>
@import './activity-app'
</style>

<style lang='sass'>
@import '../slides/slide'
</style>
